import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDataRefresh, ENTITY_TYPES } from '../context/DataRefreshContext';
import {
  X,
  AlertTriangle,
  Trash2,
  Loader2,
  ShoppingBag,
  CreditCard,
  Star,
  FileText,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatear precio
const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

/**
 * Modal para eliminar venta con análisis de dependencias
 * 
 * Props:
 * - isOpen: boolean - Controla la visibilidad del modal
 * - onClose: function - Callback al cerrar el modal
 * - venta: object - Datos de la venta a eliminar { id, numero, cliente_nombre, total_final, ... }
 * - clienteId: string (opcional) - Si se proporciona, valida que la orden pertenezca a este cliente
 * - onSuccess: function - Callback al eliminar exitosamente
 */
const EliminarVentaModal = ({ isOpen, onClose, venta, clienteId, onSuccess }) => {
  const { notifyDeletion } = useDataRefresh();
  const [loading, setLoading] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [confirmarNumero, setConfirmarNumero] = useState('');
  const [eliminando, setEliminando] = useState(false);

  // Cargar análisis al abrir el modal
  useEffect(() => {
    if (isOpen && venta?.id) {
      cargarAnalisis();
    } else {
      // Limpiar estado al cerrar
      setAnalisis(null);
      setMotivo('');
      setConfirmarNumero('');
    }
  }, [isOpen, venta?.id]);

  const cargarAnalisis = async () => {
    setAnalizando(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/gerencia/venta/${venta.id}/analisis-eliminacion`;
      
      // Si se proporciona clienteId, validar pertenencia
      if (clienteId) {
        url += `?cliente_id_expediente=${clienteId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAnalisis(response.data);
    } catch (error) {
      console.error('Error al analizar:', error);
      const mensaje = error.response?.data?.detail || 'Error al analizar la venta';
      toast.error(mensaje);
      if (error.response?.status === 403 || error.response?.status === 400) {
        // Error de permisos o validación - cerrar modal
        onClose();
      }
    } finally {
      setAnalizando(false);
    }
  };

  const handleEliminar = async () => {
    // Validar motivo
    if (motivo.trim().length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    // Validar confirmación del número
    if (confirmarNumero.trim().toLowerCase() !== venta.numero.trim().toLowerCase()) {
      toast.error(`Escriba exactamente el número de venta: "${venta.numero}"`);
      return;
    }
    
    // Verificar si puede eliminar
    if (analisis && !analisis.puede_eliminar) {
      toast.error('No se puede eliminar esta venta. Revise los bloqueos.');
      return;
    }

    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/gerencia/venta/${venta.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          motivo: motivo.trim(),
          confirmar_nombre: confirmarNumero.trim()
        }
      });
      
      toast.success(`Venta ${venta.numero} eliminada lógicamente. Puede restaurarse desde Registros Eliminados.`);
      notifyDeletion(ENTITY_TYPES.VENTAS, { cliente_id: venta.cliente_id || clienteId });
      onClose();
      if (onSuccess) {
        onSuccess(venta);
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      const mensaje = error.response?.data?.detail || 'Error al eliminar la venta';
      toast.error(mensaje);
    } finally {
      setEliminando(false);
    }
  };

  if (!isOpen) return null;

  const puedeConfirmar = 
    motivo.trim().length >= 10 && 
    confirmarNumero.trim().toLowerCase() === (venta?.numero || '').trim().toLowerCase() &&
    analisis?.puede_eliminar === true;

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !eliminando && onClose()}
    >
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="font-semibold text-white">Eliminar Venta</h2>
              <p className="text-sm text-zinc-400">{venta?.numero}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={eliminando}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Info de la venta */}
          <div className="bg-zinc-800/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Cliente:</span>
              <span className="text-white font-medium">{venta?.cliente_nombre}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total:</span>
              <span className="text-emerald-400 font-bold">{formatPrice(venta?.total_final)}</span>
            </div>
          </div>

          {/* Estado de análisis */}
          {analizando ? (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
              <span className="text-zinc-400">Analizando dependencias...</span>
            </div>
          ) : analisis ? (
            <>
              {/* Discrepancia de cliente */}
              {analisis.discrepancia_cliente && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium text-sm">Discrepancia de Cliente</p>
                      <p className="text-amber-300/80 text-xs mt-1">
                        {analisis.discrepancia_cliente.mensaje}
                      </p>
                      <p className="text-amber-300/60 text-xs mt-1">
                        Esta inconsistencia debe corregirse, no eliminarse.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bloqueos */}
              {analisis.bloqueos?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium text-sm">No se puede eliminar</p>
                      <ul className="text-red-300/80 text-xs mt-1 space-y-1">
                        {analisis.bloqueos.map((bloqueo, i) => (
                          <li key={i}>{bloqueo}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Advertencias */}
              {analisis.advertencias?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium text-sm">Advertencias</p>
                      <ul className="text-amber-300/80 text-xs mt-1 space-y-1">
                        {analisis.advertencias.map((adv, i) => (
                          <li key={i}>{adv}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Resumen de dependencias */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                    <CreditCard className="w-4 h-4" />
                    Pagos
                  </div>
                  <p className="text-white font-medium">
                    {analisis.dependencias?.pagos?.cuotas_verificadas || 0}/{analisis.dependencias?.pagos?.total_cuotas || 0} cuotas
                  </p>
                  <p className="text-green-400 text-xs">
                    Pagado: {formatPrice(analisis.dependencias?.pagos?.total_pagado)}
                  </p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                    <Star className="w-4 h-4" />
                    Puntos JPM
                  </div>
                  <p className="text-white font-medium">
                    {(analisis.dependencias?.puntos_jpm?.total_puntos_acumulados || 0).toLocaleString()} acum.
                  </p>
                  <p className="text-purple-400 text-xs">
                    {(analisis.dependencias?.puntos_jpm?.puntos_canjeados || 0).toLocaleString()} canjeados
                  </p>
                </div>
              </div>

              {/* Efectos de la eliminación */}
              {analisis.puede_eliminar && (
                <div className="bg-zinc-800/30 rounded-lg p-3">
                  <p className="text-zinc-300 text-sm font-medium mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {analisis.efecto_eliminacion?.descripcion}
                  </p>
                  <ul className="text-zinc-400 text-xs space-y-1 ml-6">
                    {analisis.efecto_eliminacion?.acciones?.slice(0, 4).map((accion, i) => (
                      <li key={i} className="list-disc">{accion}</li>
                    ))}
                  </ul>
                  <p className="text-zinc-500 text-xs mt-2 italic">
                    La venta quedará archivada y excluida de los totales activos, conservando su historial para auditoría.
                  </p>
                </div>
              )}

              {/* Formulario de confirmación */}
              {analisis.puede_eliminar && (
                <div className="space-y-3 pt-2 border-t border-zinc-800">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">
                      Motivo de eliminación <span className="text-red-400">*</span>
                    </label>
                    <textarea data-testid="eliminar-venta-motivo"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Explique por qué se elimina esta venta (mínimo 10 caracteres)"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm 
                               placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] resize-none"
                      rows={2}
                      disabled={eliminando}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">
                      Escriba <span className="text-[#D4AF37] font-mono">{venta?.numero}</span> para confirmar
                    </label>
                    <input data-testid="eliminar-venta-confirmar-numero"
                      type="text"
                      value={confirmarNumero}
                      onChange={(e) => setConfirmarNumero(e.target.value)}
                      placeholder={venta?.numero}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm 
                               placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]"
                      disabled={eliminando}
                    />
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-zinc-800">
          <button data-testid="eliminar-venta-cancelar-btn"
            onClick={onClose}
            disabled={eliminando}
            className="px-4 py-2 text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          
          {analisis?.puede_eliminar && (
            <button data-testid="eliminar-venta-confirmar-btn"
              onClick={handleEliminar}
              disabled={!puedeConfirmar || eliminando}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 
                       disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {eliminando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Eliminar Venta
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EliminarVentaModal;
